import {Icon} from "@client/components/Icon";
import {ContentModal} from "@client/components/Modal";
import type * as constants from "@client/constants";
import type {Exercise, ExerciseSet} from "@client/features/workouts";
import * as forms from "@client/forms";
import * as analytics from "@client/shared/analytics";
import {EllipsisVerticalIcon} from "@heroicons/react/20/solid";
import {classNames, server} from "@reactivated";
import React from "react";

interface OptionsProps {
    exercise: Exercise;
    readOnly?: boolean;
    alignRight?: boolean;
    items: Array<
        | {
              title: string;
              icon: constants.Icon;
              onClick?: () => void;
              selected?: boolean;
          }
        | null
        | false
    >;
}

export function Attributes(props: {exerciseSet: ExerciseSet}) {
    // Object with color names as keys and corresponding tailwind CSS classes as values
    const disabled = "bg-gray-50 text-gray-600 ring-gray-500/10";

    const colors = {
        BELT: ["Belt", "bg-red-50 text-red-700 ring-red-600/10"],
        // yellow: ["Belt", "bg-yellow-50 text-yellow-800 ring-yellow-600/20"],
        MIXED_GRIP: ["Mixed Grip", "bg-green-50 text-green-700 ring-green-600/20"],
        HOOK_GRIP: ["Hook Grip", "bg-blue-50 text-blue-700 ring-blue-700/10"],
        CHALK: ["Chalk", "bg-indigo-50 text-indigo-700 ring-indigo-700/10"],
        STRAPS: ["Straps", "bg-purple-50 text-purple-700 ring-purple-700/10"],
        // pink: "bg-pink-50 text-pink-700 ring-pink-700/10"
    };

    const toggleAttribute = (
        attribute: server.journal.workouts.ExerciseSet.output["attributes"][number],
    ) => {
        if (props.exerciseSet.attributes.includes(attribute)) {
            void analytics.track("UNSET_EXERCISE_ATTRIBUTE", {
                exercise_attribute: attribute,
            });
            props.exerciseSet.__update({
                ...props.exerciseSet,
                attributes: props.exerciseSet.attributes.filter((a) => a != attribute),
            });
        } else {
            void analytics.track("SET_EXERCISE_ATTRIBUTE", {
                exercise_attribute: attribute,
            });
            props.exerciseSet.__update({
                ...props.exerciseSet,
                attributes: [...props.exerciseSet.attributes, attribute],
            });
        }
    };

    return (
        <div className="flex justify-center space-x-1 sm:space-x-2">
            {Object.entries(colors).map(([attribute, [label, classes]], index) => (
                <button
                    onClick={() =>
                        toggleAttribute(
                            attribute as server.journal.workouts.ExerciseSet.output["attributes"][number],
                        )
                    }
                    key={index}
                    className={classNames(
                        "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset",
                        props.exerciseSet.attributes.includes(
                            attribute as server.journal.workouts.ExerciseSet.output["attributes"][number],
                        )
                            ? classes
                            : disabled,
                    )}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

export function ExerciseSetOptions(
    props: {
        exerciseSet: ExerciseSet;
        showAttributes: boolean;
    } & OptionsProps,
) {
    const [isOpen, setIsOpen] = React.useState(false);

    const active = false as boolean;

    const classes = classNames(
        "p-1.5 flex items-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100 disabled:opacity-50",
        props.exerciseSet.attributes.length > 0 ? "bg-fuchsia-100" : "bg-stone-200/70",
    );

    const form = forms.useField(
        {
            kind: "select",
            nullable: false,
            options: [
                ["VALUE", "Weight"],
                ["PERCENTAGE", "Percentage"],
                ["RPE", "RPE"],
            ],
            required: true,
        },
        {
            value: props.exerciseSet.weight_type ?? "VALUE",
            onChange: (weight_type) => props.exerciseSet.__update({weight_type}),
        },
    );

    return (
        <>
            <button
                disabled={props.readOnly}
                className={classes}
                onClick={() => setIsOpen(true)}
            >
                <span className="sr-only">Open options</span>
                <EllipsisVerticalIcon
                    className={classNames(
                        "h-5 w-5",
                        props.exerciseSet.attributes.length > 0
                            ? "text-fuchsia-800"
                            : "text-gray-600",
                    )}
                    aria-hidden="true"
                />
            </button>

            <ContentModal isOpen={isOpen} setOpen={setIsOpen}>
                <div className="space-y-2 p-2 sm:p-4">
                    {props.showAttributes && (
                        <Attributes exerciseSet={props.exerciseSet} />
                    )}
                    {(props.exercise.type.category == "WEIGHT" ||
                        props.exercise.type.category == "BODYWEIGHT" ||
                        props.exercise.type.category == "DURATION") && (
                        <forms.Widget
                            field={{
                                ...form,
                                disabledOptions:
                                    props.exercise.type.category == "DURATION"
                                        ? ["PERCENTAGE"]
                                        : [],
                            }}
                        />
                    )}
                </div>
                <ul
                    className={classNames(
                        props.alignRight == true ? "left-0" : "right-0",
                        "divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden",
                    )}
                >
                    {props.items
                        .filter(
                            (item): item is Exclude<NonNullable<typeof item>, false> =>
                                item != false && item != null,
                        )
                        .map((item, index) => (
                            <li key={index}>
                                <button
                                    onClick={() => {
                                        item.onClick?.();
                                        setIsOpen(false);
                                    }}
                                    className={classNames(
                                        active
                                            ? "bg-gray-100 text-gray-900"
                                            : "text-gray-700",
                                        "w-full whitespace-nowrap block px-4 py-3 text-sm",
                                        item.selected == true && "font-semibold",
                                    )}
                                >
                                    <Icon icon={item.icon}>{item.title}</Icon>
                                </button>
                            </li>
                        ))}
                </ul>
            </ContentModal>
        </>
    );
}
